// COMP-FORM-RUNNER (FEAT-001): یک سؤال در هر صفحه، ذخیره خودکار فقط با callback سرور، بدون localStorage.
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { AnnotatedImageField } from './AnnotatedImageField';

export interface FormRunnerOption {
  code: string;
  label: string;
}

export interface FormRunnerQuestion {
  code: string;
  type: string;
  label: string;
  required?: boolean;
  options?: FormRunnerOption[];
  maxPoints?: number;
  previousValues?: { value: string; at: string; source?: string }[];
}

export type FormRunnerWidth = 'mobile' | 'tablet' | 'desktop';

export interface FormRunnerProps {
  questions: FormRunnerQuestion[];
  answers: Record<string, string>;
  onChange: (code: string, value: string) => void;
  onSave?: (answers: Record<string, string>) => void;
  width?: FormRunnerWidth;
  requiredMark?: string;
  optionalMark?: string;
  nextLabel?: string;
  backLabel?: string;
}

const widthPx: Record<FormRunnerWidth, number> = { mobile: 360, tablet: 768, desktop: 960 };

export function FormRunner({
  questions,
  answers,
  onChange,
  onSave,
  width = 'desktop',
  requiredMark = '*',
  optionalMark = '',
  nextLabel = 'Next',
  backLabel = 'Back',
}: FormRunnerProps) {
  const [index, setIndex] = useState(0);
  const question = questions[index];
  const progress = questions.length === 0 ? 0 : Math.round(((index + 1) / questions.length) * 100);

  useEffect(() => {
    if (!onSave) {
      return undefined;
    }
    const handle = window.setTimeout(() => onSave(answers), 3000);
    return () => window.clearTimeout(handle);
  }, [answers, onSave]);

  if (!question) {
    return null;
  }

  return (
    <Box data-testid="form-runner" sx={{ width: '100%', maxWidth: widthPx[width], mx: 'auto' }}>
      <LinearProgress variant="determinate" value={progress} aria-valuenow={progress} />
      <Stack spacing={2} sx={{ mt: 2 }}>
        <Typography component="h2">
          {question.label}
          {question.required ? requiredMark : optionalMark}
        </Typography>
        {question.options && question.options.length > 0 ? (
          <Stack spacing={1}>
            {question.options.map((option) => (
              <Button
                key={option.code}
                variant={answers[question.code] === option.code ? 'contained' : 'outlined'}
                onClick={() => onChange(question.code, option.code)}
              >
                {option.label}
              </Button>
            ))}
          </Stack>
        ) : question.type === 'info' ? (
          <Typography>{question.label}</Typography>
        ) : question.type === 'annotated_image' ? (
          <AnnotatedImageField
            valueJson={answers[question.code]}
            onChange={(value) => onChange(question.code, value)}
            maxPoints={question.maxPoints ?? 50}
          />
        ) : (
          <TextField
            value={answers[question.code] ?? ''}
            onChange={(event) => onChange(question.code, event.target.value)}
            fullWidth
          />
        )}
        {question.previousValues && question.previousValues.length > 0 ? (
          <Stack spacing={0.5} data-testid="previous-values">
            {question.previousValues.map((item) => (
              <Button key={`${item.at}-${item.value}`} size="small" variant="text" onClick={() => onChange(question.code, item.value)}>
                {item.value} · {item.at}
                {item.source ? ` (${item.source})` : ''}
              </Button>
            ))}
          </Stack>
        ) : null}
        <Stack direction="row" spacing={1}>
          <Button disabled={index === 0} onClick={() => setIndex((current) => current - 1)}>
            {backLabel}
          </Button>
          <Button disabled={index >= questions.length - 1} onClick={() => setIndex((current) => current + 1)}>
            {nextLabel}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
